import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Ion } from "@/components/Ion";
import { OCard, OrigHeader, PrimaryBtn, Screen, Spinner, inputCls, useBack } from "@/components/Orig";
import { useTheme } from "@/lib/theme";

const STATUS = {
  new: ["NEW", "#39A0FF", "sparkles-outline"], reviewing: ["REVIEWING", "#FFC107", "eye-outline"], planned: ["PLANNED", "#9B59B6", "calendar-outline"],
  in_progress: ["IN PROGRESS", "#FF9800", "construct-outline"], shipped: ["SHIPPED", "#4CCC06", "checkmark-circle-outline"], declined: ["DECLINED", "#E74C3C", "close-circle-outline"],
};
const CAT_LABEL = { feature: "Feature", bug: "Bug", ui: "UI", integration: "Integration", other: "Other" };
const FILTERS = [["all", "All"], ["mine", "Mine"], ["new", "New"], ["reviewing", "Reviewing"], ["planned", "Planned"], ["in_progress", "In Progress"], ["shipped", "Shipped"], ["declined", "Declined"]];
const CATEGORIES = [["feature", "Feature", "sparkles-outline"], ["bug", "Bug", "bug-outline"], ["ui", "UI / Design", "color-palette-outline"], ["integration", "Integration", "git-network-outline"], ["other", "Other", "chatbubble-outline"]];
const PRIORITIES = [["low", "Low", "#888"], ["medium", "Medium", "#FFC107"], ["high", "High", "#E74C3C"]];

const FbHeader = ({ eyebrow, title, right, testId }) => {
  const theme = useTheme();
  const back = useBack();
  return (
    <div className="flex items-center gap-2 p-4">
      <button data-testid={testId} onClick={back} className="p-2"><Ion name="chevron-back" size={22} color={theme.text} /></button>
      <div className="flex-1"><p className="text-[11px] font-bold tracking-[3px]" style={{ color: theme.accent }}>{eyebrow}</p><h1 className="mt-0.5 text-[22px] font-extrabold" data-testid="page-title">{title}</h1></div>
      {right}
    </div>
  );
};

const FbCard = ({ it, user, onUp, onDel }) => {
  const theme = useTheme();
  const [label, color, icon] = STATUS[it.status] || STATUS.new;
  const mine = it.user_id === user.id;
  const up = it.upvoted_by.includes(user.id);
  return (
    <OCard className="mb-2" data-testid={`fb-${it.id}`}>
      <div className="mb-2 flex items-center gap-1.5">
        <span className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-extrabold tracking-[1px]" style={{ borderColor: color, background: `${color}20`, color }}><Ion name={icon} size={11} color={color} />{label}</span>
        <span className="rounded-full border px-2 py-0.5 text-[9px] font-bold" style={{ borderColor: theme.border, color: theme.textSecondary }}>{CAT_LABEL[it.category]}</span>
        <span className="flex-1" />
        <button data-testid={`upvote-${it.id}`} onClick={() => onUp(it)} disabled={mine} className="flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-extrabold disabled:opacity-40"
          style={up ? { background: theme.accent, borderColor: theme.accent, color: "#000" } : { borderColor: theme.accent, color: theme.accent }}>
          <Ion name={up ? "arrow-up-circle" : "arrow-up-circle-outline"} size={14} color={up ? "#000" : theme.accent} />{it.upvoted_by.length}
        </button>
      </div>
      <p className="line-clamp-2 text-[15px] font-bold">{it.title}</p>
      <p className="mt-1 line-clamp-3 text-[13px] leading-[19px]" style={{ color: theme.textSecondary }}>{it.description}</p>
      {it.admin_reply?.trim() && <div className="mt-2 flex gap-1.5 rounded-lg border p-2" style={{ borderColor: `${theme.accent}55`, background: `${theme.accent}0F` }}><Ion name="chatbubble-ellipses-outline" size={12} color={theme.accent} /><p className="line-clamp-2 flex-1 text-xs">{it.admin_reply}</p></div>}
      <div className="mt-2 flex items-center justify-between">
        <p className="text-[11px]" style={{ color: theme.textSecondary }}>{mine ? "You" : it.user_full_name || it.user_email} · {new Date(it.created_at).toLocaleDateString()}</p>
        {mine && <button data-testid={`fb-delete-${it.id}`} onClick={() => onDel(it)}><Ion name="trash-outline" size={13} color={theme.error} /></button>}
      </div>
    </OCard>
  );
};

export function FeedbackFeed() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState("all");
  const load = () => { setBusy(true); api("/feedback").then(setItems).catch(() => {}).finally(() => setBusy(false)); };
  useEffect(load, []);
  const filtered = useMemo(() => items.filter((i) => filter === "all" || (filter === "mine" ? i.user_id === user.id : i.status === filter)).sort((a, b) => {
    const ad = ["shipped", "declined"].includes(a.status), bd = ["shipped", "declined"].includes(b.status);
    if (ad !== bd) return ad ? 1 : -1;
    const d = (b.upvoted_by?.length || 0) - (a.upvoted_by?.length || 0);
    return d || b.created_at.localeCompare(a.created_at);
  }), [items, filter, user]);
  const onUp = async (it) => {
    setItems((arr) => arr.map((x) => x.id !== it.id ? x : { ...x, upvoted_by: x.upvoted_by.includes(user.id) ? x.upvoted_by.filter((i) => i !== user.id) : [...x.upvoted_by, user.id] }));
    try { await api(`/feedback/${it.id}/upvote`, { method: "POST" }); } catch { load(); }
  };
  const onDel = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try { await api(`/feedback/${it.id}`, { method: "DELETE" }); load(); } catch (e) { window.alert(e.message); }
  };
  return (
    <Screen testId="feedback-page">
      <FbHeader testId="feedback-back" eyebrow="HELP US IMPROVE" title="Feature Requests"
        right={<button data-testid="new-feedback-btn" onClick={() => navigate("/feedback/new")} className="flex items-center gap-1 rounded-lg px-3 py-2 text-[11px] font-extrabold tracking-[1px] text-black" style={{ background: theme.accent }}><Ion name="add" size={16} color="#000" />NEW</button>} />
      <div className="grid grid-cols-4 gap-1.5 px-4 pb-3">
        {FILTERS.map(([id, label]) => (
          <button key={id} data-testid={`fb-filter-${id}`} onClick={() => setFilter(id)} className="truncate rounded-full border px-2 py-1.5 text-[11px] font-bold"
            style={filter === id ? { background: theme.accent, borderColor: theme.accent, color: "#000" } : { borderColor: theme.border, color: theme.textSecondary, background: theme.card }}>{label}</button>
        ))}
      </div>
      <div className="px-4 pb-16">
        {busy && <Spinner />}
        {!busy && filtered.length === 0 && (
          <div className="mt-6 flex flex-col items-center gap-2 rounded-[14px] border p-6 text-center" style={{ borderColor: theme.border }} data-testid="feedback-empty">
            <Ion name="bulb-outline" size={28} color={theme.textSecondary} />
            <p className="text-[15px] font-bold">{filter === "mine" ? "You haven't submitted any requests yet" : "No requests yet"}</p>
            <p className="text-[13px]" style={{ color: theme.textSecondary }}>Tap NEW to share an idea or report a bug. Every submission goes straight to ALR.</p>
            <button data-testid="empty-new-btn" onClick={() => navigate("/feedback/new")} className="mt-2 rounded-lg px-4 py-2.5 text-xs font-extrabold tracking-[1px] text-black" style={{ background: theme.accent }}>SUBMIT FIRST REQUEST</button>
          </div>
        )}
        {filtered.map((it) => <FbCard key={it.id} it={it} user={user} onUp={onUp} onDel={onDel} />)}
      </div>
    </Screen>
  );
}

const Field = ({ children }) => <p className="mb-2 mt-4 text-xs font-bold text-[#A0A0A0]">{children}</p>;

export function NewFeedback() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("feature");
  const [priority, setPriority] = useState("medium");
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => { const b64 = String(r.result).split(",")[1] || ""; if (b64.length > 4_000_000) window.alert("Screenshot is too large. Try a smaller image."); else setShot(b64); };
    r.readAsDataURL(f);
  };
  const submit = async () => {
    if (!title.trim()) return window.alert("Please enter a title.");
    if (!description.trim()) return window.alert("Please describe your idea.");
    setBusy(true);
    try {
      await api("/feedback", { method: "POST", body: { title: title.trim(), description: description.trim(), category, priority, screenshot_b64: shot || undefined } });
      window.alert("Thanks! Your request was sent to ALR. We'll review it shortly.");
      navigate("/feedback", { replace: true });
    } catch (e) { window.alert(e.message || "Could not submit. Try again."); } finally { setBusy(false); }
  };
  const chip = (active, c) => active ? { background: c || theme.accent, borderColor: c || theme.accent, color: "#000" } : { borderColor: theme.border, color: theme.text, background: theme.card };
  return (
    <Screen testId="new-feedback-page">
      <FbHeader testId="new-fb-back" eyebrow="SUBMIT A REQUEST" title="New Idea" />
      <div className="px-4 pb-40">
        <p className="text-[13px] leading-5" style={{ color: theme.textSecondary }}>Tell ALR what would make the portal better. Greg gets notified as soon as you submit. Other users can upvote your idea.</p>
        <Field>Category</Field>
        <div className="flex flex-wrap gap-2">{CATEGORIES.map(([id, label, icon]) => <button key={id} data-testid={`cat-${id}`} onClick={() => setCategory(id)} className="flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-bold" style={chip(category === id)}><Ion name={icon} size={13} color={category === id ? "#000" : theme.accent} />{label}</button>)}</div>
        <Field>Priority</Field>
        <div className="flex flex-wrap gap-2">{PRIORITIES.map(([id, label, c]) => <button key={id} data-testid={`pri-${id}`} onClick={() => setPriority(id)} className="rounded-full border px-3 py-2 text-xs font-bold" style={chip(priority === id, c)}>{label}</button>)}</div>
        <Field>Title *</Field>
        <input data-testid="fb-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Short, descriptive name for your idea" className={inputCls} />
        <Field>Description *</Field>
        <textarea data-testid="fb-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What problem does this solve? How should it work?" className={`${inputCls} min-h-[120px]`} />
        <Field>Screenshot (optional)</Field>
        {shot ? (
          <div className="relative w-40"><img src={`data:image/png;base64,${shot}`} alt="screenshot" className="h-40 w-40 rounded-lg object-cover" /><button data-testid="remove-screenshot" onClick={() => setShot(null)} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70"><Ion name="close" size={14} color="#fff" /></button></div>
        ) : (
          <label data-testid="pick-screenshot" className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed py-4 text-xs font-extrabold tracking-[1px]" style={{ borderColor: theme.accent, color: theme.accent }}>
            <Ion name="image-outline" size={18} color={theme.accent} />ATTACH SCREENSHOT<input type="file" accept="image/*" className="hidden" onChange={pick} />
          </label>
        )}
      </div>
      <div className="fixed inset-x-0 bottom-0 border-t p-4 min-[900px]:left-[264px]" style={{ background: theme.bg, borderColor: theme.border }}>
        <PrimaryBtn data-testid="submit-fb-btn" onClick={submit} disabled={busy}>{busy ? <Loader2 className="animate-spin" size={18} /> : <><Ion name="send" size={16} color="#000" />SEND TO ALR</>}</PrimaryBtn>
      </div>
    </Screen>
  );
}

export function QuizScreen() {
  const { id } = useParams();
  const theme = useTheme();
  const back = useBack();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api(`/quizzes/${id}`).then(setQuiz).catch(() => {}).finally(() => setLoading(false)); }, [id]);
  if (loading) return <Screen><Spinner /></Screen>;
  if (!quiz) return <Screen><p className="mt-10 text-center text-[#A0A0A0]" data-testid="quiz-not-found">Quiz not found.</p></Screen>;
  const complete = Object.keys(answers).length >= quiz.questions.length;
  const submit = async () => {
    if (!complete) return;
    setBusy(true);
    try { setResult(await api(`/quizzes/${id}/submit`, { method: "POST", body: { quiz_id: id, answers: quiz.questions.map((_, i) => answers[i]) } })); } finally { setBusy(false); }
  };
  const ok = result?.passed ? theme.success : theme.error;
  return (
    <Screen testId="quiz-page">
      <OrigHeader eyebrow={quiz.is_final ? "FINAL TEST" : "QUIZ"} title={quiz.title} />
      <div className="px-6 pb-8">
        {result ? (
          <div className="flex flex-col items-center gap-2 rounded-[14px] border-2 p-6" style={{ borderColor: ok, background: theme.card }} data-testid="quiz-result">
            <Ion name={result.passed ? "trophy" : "close-circle"} size={48} color={ok} />
            <p className="text-[13px] font-extrabold tracking-[3px]">{result.passed ? "PASSED" : "TRY AGAIN"}</p>
            <p className="text-5xl font-extrabold">{result.score}%</p>
            <p className="text-[13px]" style={{ color: theme.textSecondary }}>{result.correct} / {result.total} correct • Passing score {quiz.pass_score}%</p>
            <PrimaryBtn data-testid="quiz-retry" className="mt-4" onClick={() => { setResult(null); setAnswers({}); }}>{result.passed ? "REVIEW AGAIN" : "RETRY QUIZ"}</PrimaryBtn>
            <button onClick={back} className="w-full rounded-lg border py-3.5 text-[13px] font-extrabold tracking-[1.5px]" style={{ borderColor: theme.border }}>BACK TO TRAINING</button>
          </div>
        ) : <>
          <p className="mb-6 text-[13px] leading-5" style={{ color: theme.textSecondary }}>{quiz.description}</p>
          {quiz.questions.map((q, i) => (
            <OCard key={i} className="mb-4" data-testid={`question-${i}`}>
              <p className="text-[10px] font-bold tracking-[2px]" style={{ color: theme.accent }}>QUESTION {i + 1}</p>
              <p className="mb-3 mt-1 text-[15px] font-bold">{q.question}</p>
              {q.options.map((opt, j) => {
                const sel = answers[i] === j;
                return (
                  <button key={j} data-testid={`q${i}-opt${j}`} onClick={() => setAnswers({ ...answers, [i]: j })} className="mb-2 flex w-full items-center gap-3 rounded-lg border p-3 text-left" style={{ borderColor: sel ? theme.accent : theme.border, background: sel ? `${theme.accent}12` : "transparent" }}>
                    <span className="h-4 w-4 shrink-0 rounded-full border-2" style={sel ? { borderColor: theme.accent, background: theme.accent } : { borderColor: theme.border }} />
                    <span className="text-sm" style={{ color: sel ? theme.text : theme.textSecondary }}>{opt}</span>
                  </button>
                );
              })}
            </OCard>
          ))}
          <PrimaryBtn data-testid="quiz-submit" onClick={submit} disabled={busy || !complete}>{busy ? "SUBMITTING…" : "SUBMIT QUIZ"}</PrimaryBtn>
        </>}
      </div>
    </Screen>
  );
}
